/**
 * banner.ts — consent + status UI for habitat sensing.
 *
 * Banner and pill share one fixed, bottom-centered overlay so they never
 * jump position, and that overlay is headroom'd: it slides out of the way
 * on scroll-down and returns on scroll-up, same as the page header.
 *
 * The actual consent state machine lives in consent.ts; this module is
 * just the DOM shell that reads/writes localStorage through it.
 */
import { CONSENT_KEY, nextConsentState, readConsentState, shouldAutoStart, shouldShowBanner } from "./consent.js";
import { headroom } from "./headroom.js";
import type { HabitatSensor } from "./sensor.js";
import type { HabitatStateDetail } from "./types.js";

const CAMERA_ICON = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"
  stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
  stroke-linejoin="round" aria-hidden="true">
  <path d="M23 7l-7 5 7 5V7z"/>
  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`;

export interface HabitatBannerController {
  /** Call when a mode wanting the camera is selected. Respects earlier
      "Not now" / Stop decisions — those are only reversed by resume(). */
  request(): void;
  /** Explicit user action (e.g. privacy dialog): re-grant and start. */
  resume(): Promise<void>;
}

export function initHabitatBanner(sensor: HabitatSensor): HabitatBannerController {
  const overlay = document.createElement("div");
  overlay.className = "habitat-overlay-bottom habitat-headroom--bottom";
  document.body.appendChild(overlay);
  headroom(overlay);

  let banner: HTMLElement | null = null;
  let pill: HTMLElement | null = null;

  const consentState = () => readConsentState(localStorage.getItem(CONSENT_KEY));

  function showBanner(): void {
    if (banner || sensor.sensing) return;
    banner = document.createElement("aside");
    banner.className = "habitat-consent";
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-live", "polite");
    banner.setAttribute("aria-label", "Habitat sensing permission");
    banner.innerHTML = `
      <div class="habitat-consent__icon">${CAMERA_ICON}</div>
      <div>
        <p class="habitat-consent__title">Let this page sense the light around you?</p>
        <p class="habitat-consent__body">
          To adapt to your surroundings, this page takes a
          <strong>tiny camera snapshot every 15 seconds</strong>. Each snapshot
          is <strong>blurred as it is captured</strong>, analyzed
          <strong>entirely in your browser</strong>, and
          <strong>erased immediately</strong> — at most one blurred frame
          exists at any moment, and no image is ever shown, stored, or sent
          anywhere. Only the results — an ambient brightness level and a few
          palette colors — are kept, on this device. Stop or forget everything
          anytime.
        </p>
        <div class="habitat-consent__actions">
          <button class="habitat-btn habitat-btn--primary" data-act="allow">
            Allow habitat sensing
          </button>
          <button class="habitat-btn habitat-btn--ghost" data-act="dismiss">
            Not now
          </button>
        </div>
      </div>`;
    banner.addEventListener("click", async (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act === "allow") {
        try {
          await sensor.start(); // triggers the browser's own camera prompt
          localStorage.setItem(CONSENT_KEY, nextConsentState(consentState(), "allow"));
          hideBanner();
        } catch (err) {
          console.warn("[habitat] camera unavailable:", (err as Error).message);
          localStorage.setItem(CONSENT_KEY, nextConsentState(consentState(), "dismiss"));
          hideBanner();
          sensor.applyFallback();
        }
      } else if (act === "dismiss") {
        localStorage.setItem(CONSENT_KEY, nextConsentState(consentState(), "dismiss"));
        hideBanner();
        sensor.applyFallback();
      }
    });
    overlay.appendChild(banner);
    banner.querySelector<HTMLElement>("[data-act='allow']")?.focus({ preventScroll: true });
  }

  function hideBanner(): void {
    banner?.remove();
    banner = null;
  }

  function showPill(): void {
    if (pill) return;
    pill = document.createElement("div");
    pill.className = "habitat-pill";
    pill.innerHTML = `
      <span class="habitat-pill__dot" aria-hidden="true"></span>
      <span>Camera sensing ambient light</span>
      <button class="habitat-btn habitat-btn--ghost" data-act="stop">Stop</button>
      <button class="habitat-btn habitat-btn--ghost" data-act="forget"
        title="Stop and erase all persisted habitat data">Forget</button>`;
    pill.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act === "stop") {
        sensor.stop();
        // Stopping is a decision, not a pause: remember it so mode
        // switches never silently restart the camera.
        localStorage.setItem(CONSENT_KEY, nextConsentState(consentState(), "stop"));
      }
      if (act === "forget") sensor.forget();
    });
    overlay.appendChild(pill);
  }

  function hidePill(): void {
    pill?.remove();
    pill = null;
  }

  sensor.addEventListener("habitatstate", ((e: CustomEvent<HabitatStateDetail>) => {
    e.detail.sensing ? showPill() : hidePill();
  }) as EventListener);

  return {
    request() {
      if (sensor.sensing) return;
      const state = consentState();
      if (shouldAutoStart(state)) {
        sensor.start().catch((err: Error) => {
          console.warn("[habitat] camera unavailable:", err.message);
          sensor.applyFallback();
        });
      } else if (shouldShowBanner(state)) {
        showBanner();
      }
      // "dismissed" / "stopped": the user already decided — do nothing.
    },

    async resume() {
      hideBanner();
      try {
        await sensor.start();
        localStorage.setItem(CONSENT_KEY, nextConsentState(consentState(), "allow"));
      } catch (err) {
        console.warn("[habitat] camera unavailable:", (err as Error).message);
        sensor.applyFallback();
      }
    },
  };
}
